import { useEffect, useState } from 'react';
import { Bell, BellOff, Loader2, CheckCircle2, Smartphone } from 'lucide-react';
import { getPushStatus, enablePush, disablePush, testPush } from '../lib/push';
import { api } from '../lib/api';
import { useI18n } from '../i18n';
import { toast } from 'sonner';

export default function PushSettingsCard() {
    const { t } = useI18n();
    const [status, setStatus] = useState({ supported: false, granted: false, subscribed: false, loading: true });
    const [busy, setBusy] = useState(false);
    const [native, setNative] = useState({ count: 0, tokens: [] });

    const refresh = async () => {
        const s = await getPushStatus();
        setStatus({ ...s, loading: false });
        try {
            const r = await api.get('/push/native/me');
            setNative(r.data || { count: 0, tokens: [] });
        } catch { /* ignore */ }
    };
    useEffect(() => { refresh(); }, []);

    if (status.loading) return null;

    // On Capacitor native: web-push isn't supported, but the native push pipeline
    // runs independently via @capacitor/push-notifications. Show native section directly.
    const isNative = typeof window !== 'undefined'
        && !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());

    if (!status.supported && !isNative) return (
        <section className="mb-8" data-testid="push-card-unsupported">
            <div className="bg-brand-card border border-brand-border rounded-3xl p-4 text-sm text-brand-ink3">
                {t('push.unsupported')}
            </div>
        </section>
    );

    const toggle = async () => {
        setBusy(true);
        try {
            if (status.subscribed) {
                await disablePush();
                toast.success(t('push.off_ok'));
            } else {
                await enablePush();
                toast.success(t('push.on_ok'));
            }
            await refresh();
        } catch (e) {
            toast.error(e.message || t('push.err'));
        } finally { setBusy(false); }
    };

    const test = async () => {
        setBusy(true);
        try {
            const sent = await testPush();
            if (sent > 0) toast.success(t('push.test_ok'));
            else toast.error(t('push.test_none'));
        } catch (e) {
            toast.error(t('push.test_fail'));
        } finally { setBusy(false); }
    };

    return (
        <section className="mb-8" data-testid="push-settings-card">
            <h2 className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-3 inline-flex items-center gap-1">
                <Bell size={11} /> {t('push.header')}
            </h2>
            {!isNative && (
            <div className="bg-brand-card border border-brand-border rounded-3xl p-5">
                <div className="flex items-start gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${status.subscribed ? 'bg-brand-green/15 text-brand-green' : 'bg-brand-bg2 text-brand-ink3'}`}>
                        {status.subscribed ? <Bell size={18} strokeWidth={1.8} /> : <BellOff size={18} strokeWidth={1.8} />}
                    </div>
                    <div className="flex-1">
                        <div className="font-serif text-base text-brand-ink leading-snug">
                            {t('push.water_frost')}
                        </div>
                        <div className="text-xs text-brand-ink3 mt-1">
                            {t('push.water_frost_sub')}
                        </div>
                    </div>
                    {status.subscribed && <CheckCircle2 size={18} className="text-brand-green flex-shrink-0" />}
                </div>

                {status.denied && (
                    <div className="text-xs text-brand-terracotta bg-brand-terracotta/10 border border-brand-terracotta/20 rounded-2xl p-3 mb-3">
                        {t('push.denied')}
                    </div>
                )}

                <div className="flex gap-2">
                    <button
                        data-testid="btn-push-toggle"
                        onClick={toggle}
                        disabled={busy || status.denied}
                        className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-sm transition-colors ${status.subscribed ? 'bg-brand-card border border-brand-border text-brand-ink hover:bg-brand-bg2' : 'bg-brand-green text-white hover:bg-brand-greenDark'} disabled:opacity-50`}
                    >
                        {busy ? <Loader2 size={14} className="animate-spin" /> : (status.subscribed ? t('push.turn_off') : t('push.enable'))}
                    </button>
                    {status.subscribed && (
                        <button
                            data-testid="btn-push-test"
                            onClick={test}
                            disabled={busy}
                            className="inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-full text-sm bg-brand-bg2 border border-brand-border text-brand-ink hover:bg-brand-bg disabled:opacity-50"
                        >
                            {t('push.send_test')}
                        </button>
                    )}
                </div>
            </div>
            )}

            {/* Native (Capacitor) status: always shown on native, lists token + test button */}
            {isNative && (
                <div className="bg-brand-card border border-brand-border rounded-3xl p-5" data-testid="native-push-status">
                    <div className="flex items-start gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${native.count > 0 ? 'bg-brand-green/15 text-brand-green' : 'bg-brand-bg2 text-brand-ink3'}`}>
                            <Smartphone size={18} strokeWidth={1.8} />
                        </div>
                        <div className="flex-1">
                            <div className="font-serif text-base text-brand-ink leading-snug">{t('push.native_title')}</div>
                            <div className="text-xs text-brand-ink3 mt-1">
                                {native.count > 0 ? t('push.native_on') : t('push.native_off')}
                            </div>
                        </div>
                        {native.count > 0 && <CheckCircle2 size={18} className="text-brand-green flex-shrink-0" />}
                    </div>
                    {native.tokens.map((tok) => (
                        <div key={`${tok.platform}-${tok.token_preview}`} className="text-[10px] font-mono text-brand-ink3 break-all mb-1">
                            <span className="uppercase">{tok.platform}</span> · {tok.token_preview} <span className="opacity-60">({tok.token_length}b)</span>
                        </div>
                    ))}
                    {native.count === 0 && (
                        <button
                            data-testid="btn-native-enable"
                            onClick={async () => {
                                setBusy(true);
                                try {
                                    const { ensureNativePush } = await import('../lib/nativePush');
                                    const res = await ensureNativePush();
                                    if (res.error === 'permission-denied') {
                                        toast.error('Notification permission denied. Enable manually in Android Settings → Apps → GrowDaily.');
                                    } else if (res.error) {
                                        toast.error(`Native push error: ${res.error}`);
                                    } else {
                                        toast.success('Push enabled — waiting for FCM token…');
                                        setTimeout(refresh, 2500);
                                    }
                                } catch (e) {
                                    toast.error(e?.message || 'Failed to enable native push');
                                } finally { setBusy(false); }
                            }}
                            disabled={busy}
                            className="mt-2 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-sm bg-brand-green hover:bg-brand-greenDark text-white disabled:opacity-50"
                        >
                            {busy ? <Loader2 size={14} className="animate-spin" /> : t('push.native_enable')}
                        </button>
                    )}
                    {native.count > 0 && (
                        <button
                            data-testid="btn-native-test"
                            onClick={async () => {
                                setBusy(true);
                                try {
                                    const r = await api.post('/push/native/test-self');
                                    if (r.data.sent > 0) toast.success(`Native push sent (${r.data.sent}/${r.data.total})`);
                                    else toast.error(`No native push delivered. Failed: ${JSON.stringify(r.data.failed)}`);
                                } catch (e) {
                                    toast.error(e?.response?.data?.detail || 'Native test failed');
                                } finally { setBusy(false); }
                            }}
                            disabled={busy}
                            className="mt-2 w-full inline-flex items-center justify-center gap-2 py-2 rounded-full text-xs bg-brand-bg2 border border-brand-border text-brand-ink hover:bg-brand-bg disabled:opacity-50"
                        >
                            {busy ? <Loader2 size={12} className="animate-spin" /> : t('push.native_test')}
                        </button>
                    )}
                    <button
                        data-testid="btn-native-refresh"
                        onClick={refresh}
                        className="mt-2 w-full text-xs text-brand-ink3 hover:text-brand-ink2 py-1"
                    >
                        {t('push.native_refresh')}
                    </button>
                </div>
            )}
        </section>
    );
}
